import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Ticket, Copy, Download, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminService } from "@/services/adminService";
import { apiError } from "@/lib/api";

export function WaitlistPanel() {
  const qc = useQueryClient();
  const { data: waitlist = [], isLoading } = useQuery({ queryKey: ["admin", "waitlist"], queryFn: adminService.getWaitlist });
  const { data: invites = [] } = useQuery({ queryKey: ["admin", "invites"], queryFn: adminService.getInvites });
  const [email, setEmail] = useState("");
  const create = useMutation({
    mutationFn: adminService.createInvite,
    onSuccess: (d) => {
      toast.success(`Invite code created: ${d.code}`);
      setEmail("");
      qc.invalidateQueries({ queryKey: ["admin", "invites"] });
      qc.invalidateQueries({ queryKey: ["admin", "waitlist"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const exportCsv = () => {
    const rows = [["email", "joined", "invited"], ...waitlist.map((w) => [w.email, w.created_at || "", w.invited ? "yes" : "no"])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "waitlist.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="waitlist-panel">
      {/* Generate invite */}
      <div className="border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-1"><Ticket className="h-4 w-4 text-primary" /><h3 className="font-display font-bold text-sm">Generate single-use invite</h3></div>
        <p className="text-xs text-muted-foreground mb-3">Creates a one-time code (Scholar + 100 coins). Optionally tie it to a waitlist email.</p>
        <div className="flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email (optional)" className="rounded-none h-10" data-testid="invite-email-input" />
          <Button onClick={() => create.mutate({ email: email.trim() || null })} disabled={create.isPending} className="rounded-none h-10 gap-2 shrink-0" data-testid="invite-create-btn">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />} Create code
          </Button>
        </div>
      </div>

      {/* Invite codes list */}
      <div>
        <h3 className="font-display font-bold text-sm mb-2">Invite codes ({invites.length})</h3>
        <div className="border border-border divide-y max-h-64 overflow-y-auto">
          {invites.length === 0 && <p className="text-xs text-muted-foreground p-3">No invite codes yet.</p>}
          {invites.map((iv) => (
            <div key={iv.code} className="flex items-center gap-3 p-2.5 text-sm" data-testid={`invite-row-${iv.code}`}>
              <code className="font-mono font-bold">{iv.code}</code>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${iv.used ? "border-muted text-muted-foreground" : "border-emerald-500 text-emerald-600 bg-emerald-500/10"}`}>
                {iv.used ? "USED" : "ACTIVE"}
              </span>
              <span className="text-xs text-muted-foreground truncate">{iv.used ? iv.used_by_email : iv.email || "—"}</span>
              {!iv.used && <button onClick={() => copy(iv.code)} className="ml-auto text-muted-foreground hover:text-foreground" data-testid={`invite-copy-${iv.code}`}><Copy className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Waitlist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Waitlist ({waitlist.length})</h3>
          <Button variant="outline" size="sm" className="rounded-none gap-1.5" onClick={exportCsv} disabled={!waitlist.length} data-testid="waitlist-export-csv">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
        {isLoading ? <p className="text-xs text-muted-foreground">Loading…</p> : (
          <div className="border border-border divide-y max-h-80 overflow-y-auto">
            {waitlist.length === 0 && <p className="text-xs text-muted-foreground p-3">No signups yet.</p>}
            {waitlist.map((w) => (
              <div key={w.email} className="flex items-center gap-3 p-2.5 text-sm" data-testid={`waitlist-row-${w.email}`}>
                <span className="truncate">{w.email}</span>
                {w.invited && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> invited</span>}
                <button onClick={() => create.mutate({ email: w.email })} className="ml-auto text-xs font-semibold text-primary hover:underline shrink-0" data-testid={`waitlist-invite-${w.email}`}>
                  Send invite
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
